import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { conditionalUtils, gradeBenchPanelActions } from '@codametrix/ui-common';
import { FinalCodesetPanel } from './FinalCodesetPanel';
import { useLiveScore } from './useScoringEvents';
import { ScoringEventsContext } from './ScoringEventsContext';

type Props = {
    capabilities: any;
};

const CODE_TYPE_ORDER: Record<string, number> = { PROCEDURE: 0, MODIFIER: 1, DIAGNOSIS: 2 };

function sortByCodeType(codes: CQA.ResolverCodeDto[]): CQA.ResolverCodeDto[] {
    return [...codes].sort(
        (a, b) => (CODE_TYPE_ORDER[a.codeType] ?? 99) - (CODE_TYPE_ORDER[b.codeType] ?? 99)
    );
}

export function FinalCodesetPanelContainer({ capabilities }: Props): React.ReactElement | null {
    const dispatch = useDispatch();

    const sdCaseId = useSelector(
        (state: any) => state.gradeBenchPanel?.invoices?.[0]?.sdCaseId as string | undefined
    );
    const cqaInvoice = useSelector(
        (state: any) => (state.gradeBenchPanel?.invoices?.[0] ?? null) as Accelerate.CQAInvoice | null
    );
    const resolverIdealCodes = useSelector(
        (state: any) => state.gradeBenchPanel?.resolverIdealCodes as CQA.ResolverCodeDto[] | null ?? null
    );
    const resolverIsRunning = useSelector(
        (state: any) => state.gradeBenchPanel?.resolverIsRunning as boolean ?? false
    );
    const rubricVersionId = useSelector(
        (state: any) => state.gradeBenchPanel?.qaRule?.data?.project_configuration?.rubricVersionId as number | undefined
    );

    const canViewScore = conditionalUtils.capabilityValue(
        capabilities?.capabilities?.viewScoreDetail?.on
    );

    const [orderedCodes, setOrderedCodes] = useState<CQA.ResolverCodeDto[] | null>(
        resolverIdealCodes ? sortByCodeType(resolverIdealCodes) : null
    );
    useEffect(() => {
        setOrderedCodes(resolverIdealCodes ? sortByCodeType(resolverIdealCodes) : null);
    }, [resolverIdealCodes]);

    const handleReorder = (fromIdx: number, toIdx: number) => {
        const current = orderedCodes ?? [];
        const fromCode = current[fromIdx];
        const toCode = current[toIdx];

        // Reorder is only allowed within the same code type.
        if (!fromCode || !toCode || fromCode.codeType !== toCode.codeType) return;

        const next = [...current];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        setOrderedCodes(next);

        const transaction = cqaInvoice?.transactions?.[0];
        if (!transaction || !cqaInvoice) return;

        const codeType = fromCode.codeType;

        // Swap the sequence numbers that the ideal codes of this type currently occupy,
        // preserving the positions of non-ideal codes within the same type.
        const idealOfType = current.filter(c => c.codeType === codeType && c.id != null);
        const idealSeqNums = idealOfType.map(c => c.sequenceNumber).sort((a, b) => a - b);

        const idealSeqMap = new Map<number, number>();
        next.filter(c => c.codeType === codeType && c.id != null).forEach((c, i) => {
            idealSeqMap.set(c.id!, idealSeqNums[i]);
        });

        // Send only the affected code type — the others are unchanged.
        const procedures: Accelerate.CQAProcedure[] = codeType === 'PROCEDURE'
            ? transaction.procedures.map(p => ({
                ...p,
                sequenceNumber: idealSeqMap.has(p.id) ? idealSeqMap.get(p.id)! : p.sequenceNumber
              }))
            : [];
        const diagnoses: Accelerate.CQADiagnosis[] = codeType === 'DIAGNOSIS'
            ? transaction.diagnoses.map(d => ({
                ...d,
                sequenceNumber: idealSeqMap.has(d.id) ? idealSeqMap.get(d.id)! : d.sequenceNumber
              }))
            : [];
        const modifiers: Accelerate.CQAModifier[] = codeType === 'MODIFIER'
            ? transaction.modifiers.map(m => ({
                ...m,
                sequenceNumber: idealSeqMap.has(m.id) ? idealSeqMap.get(m.id)! : m.sequenceNumber
              }))
            : [];

        dispatch(gradeBenchPanelActions.bulkResequenceCodes({
            procedures,
            diagnoses,
            modifiers,
            context: { invoice: cqaInvoice, transaction }
        }) as any);
    };

    const [refetchKey, setRefetchKey] = useState(0);
    const prevResolverRunning = useRef(false);
    useEffect(() => {
        if (prevResolverRunning.current && !resolverIsRunning) {
            setRefetchKey(k => k + 1);
        }
        prevResolverRunning.current = resolverIsRunning;
    }, [resolverIsRunning]);

    const { liveScore } = useLiveScore(sdCaseId, rubricVersionId, refetchKey);

    if (rubricVersionId == null) return null;

    return (
        <ScoringEventsContext.Provider value={liveScore}>
            <FinalCodesetPanel
                idealCodes={orderedCodes}
                isResolving={resolverIsRunning}
                canViewScore={canViewScore}
                onReorder={handleReorder}
            />
        </ScoringEventsContext.Provider>
    );
}

export default FinalCodesetPanelContainer;
